import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, Copy, Search, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft: "border-zinc-700 text-zinc-400",
  scheduled: "border-blue-500/40 text-blue-400",
  sending: "border-amber-500/40 text-amber-400",
  sent: "border-emerald-500/40 text-emerald-400",
};

const PAGE_SIZE = 50;

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const nav = useNavigate();
  const load = () => api.get("/campaigns").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onDelete = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("Delete campaign?")) return;
    await api.delete(`/campaigns/${id}`); toast.success("Deleted"); load();
  };

  const onDuplicate = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const { data } = await api.post(`/campaigns/${id}/duplicate`);
      toast.success("Duplicated — opening edit mode");
      nav(`/campaigns/${data.id}/edit`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (!q) return true;
      return (c.name || "").toLowerCase().includes(q) || (c.list_name || "").toLowerCase().includes(q) || (c.owner_name || "").toLowerCase().includes(q);
    });
  }, [items, query, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="p-8 lg:p-10 max-w-[1400px] cf-fade">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8 pb-6 border-b border-zinc-800">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// dispatch</div>
          <h1 className="cf-page-title mt-2">Campaigns</h1>
          <div className="font-mono text-xs text-zinc-500 mt-2">
            {items.length} total &middot; {items.filter((c) => c.status === "draft").length} drafts &middot; {items.filter((c) => c.status === "sent").length} sent
          </div>
        </div>
        <button onClick={() => nav("/campaigns/new")} data-testid="new-campaign-button" className="cf-btn-primary"><Plus size={14} /> New Campaign</button>
      </div>

      {items.length === 0 ? (
        <div className="cf-card p-16 text-center"><div className="font-mono text-xs uppercase tracking-widest text-zinc-600">// no campaigns yet</div></div>
      ) : (
        <div className="cf-card overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap bg-[#0A0A0A]">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input type="text" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} placeholder="Search campaigns, list or owner..." className="cf-input" style={{ paddingLeft: 34 }} data-testid="campaign-search-input" />
            </div>
            <div className="flex items-center gap-1 flex-wrap" data-testid="campaign-status-filter">
              {["all", "draft", "scheduled", "sending", "sent"].map((s) => (
                <button key={s} type="button" onClick={() => { setStatusFilter(s); setPage(1); }} data-testid={`status-filter-${s}`}
                  className={`px-2.5 py-1 border text-[10px] font-mono uppercase tracking-widest transition-colors ${statusFilter === s ? "border-white bg-zinc-900 text-white" : "border-zinc-800 text-zinc-500 hover:text-white"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="cf-data-table" style={{ minWidth: 980 }} data-testid="campaigns-table">
              <colgroup><col style={{ width: "30%" }} /><col style={{ width: "18%" }} /><col style={{ width: "14%" }} /><col style={{ width: "10%" }} /><col style={{ width: "7%" }} /><col style={{ width: "7%" }} /><col style={{ width: "7%" }} /><col style={{ width: "7%" }} /></colgroup>
              <thead><tr><th>Campaign</th><th>List</th><th>Owner</th><th>Status</th><th style={{ textAlign: "right" }}>Sent</th><th style={{ textAlign: "right" }}>Open</th><th style={{ textAlign: "right" }}>Click</th><th style={{ textAlign: "right" }}>&middot;</th></tr></thead>
              <tbody>
                {paged.length === 0 ? (<tr><td colSpan={8} className="text-center text-zinc-600 py-10 font-mono text-xs uppercase">// no matches</td></tr>) : paged.map((c) => {
                  const openRate = c.stats?.sent ? Math.round((c.stats.opened / c.stats.sent) * 100) : null;
                  const statusCls = STATUS_STYLES[c.status] || "border-zinc-700 text-zinc-500";
                  return (
                    <tr key={c.id} onClick={() => nav(`/campaigns/${c.id}`)} data-testid={`campaign-row-${c.id}`} className="cursor-pointer">
                      <td title={c.name}><div className="text-white truncate font-medium">{c.name}</div><div className="cf-cell-secondary truncate">{c.steps?.length || 1} step{(c.steps?.length || 1) === 1 ? "" : "s"}{c.ab_test_enabled && <span className="ml-2 text-[#E23A28]">&middot; A/B</span>}</div></td>
                      <td title={c.list_name}><span className="truncate block text-zinc-300">{c.list_name || <em className="text-zinc-700">—</em>}</span></td>
                      <td title={c.owner_name}><span className="font-mono text-xs text-zinc-500 truncate block">{c.owner_name || "—"}</span></td>
                      <td><span className={`inline-block px-1.5 py-0.5 border text-[9px] font-mono uppercase tracking-widest ${statusCls}`}>{c.status}</span></td>
                      <td style={{ textAlign: "right" }} className="font-mono tabular-nums text-zinc-200">{c.stats?.sent || 0}</td>
                      <td style={{ textAlign: "right" }} className="font-mono tabular-nums text-zinc-200">{c.stats?.opened || 0}{openRate != null && <span className="ml-1 text-[10px] text-zinc-500">({openRate}%)</span>}</td>
                      <td style={{ textAlign: "right" }} className="font-mono tabular-nums text-zinc-200">{c.stats?.clicked || 0}</td>
                      <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end items-center gap-2">
                          {c.status === "draft" && <Link to={`/campaigns/${c.id}/edit`} data-testid={`edit-campaign-${c.id}`} title="Edit draft" className="text-zinc-500 hover:text-white"><Pencil size={13} /></Link>}
                          <button onClick={(e) => onDuplicate(c.id, e)} data-testid={`duplicate-campaign-${c.id}`} title="Duplicate" className="text-zinc-500 hover:text-white"><Copy size={13} /></button>
                          <button onClick={(e) => onDelete(c.id, e)} data-testid={`delete-campaign-${c.id}`} title="Delete" className="text-zinc-500 hover:text-[#E23A28]"><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-zinc-800 flex items-center justify-between gap-3 bg-[#0A0A0A] flex-wrap" data-testid="campaigns-pagination">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}</div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} data-testid="page-prev" className="px-3 py-1 border border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">← Prev</button>
                <span className="px-2 font-mono text-[11px] text-white tabular-nums">{safePage} / {totalPages}</span>
                <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} data-testid="page-next" className="px-3 py-1 border border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">Next →</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
